/**
 * Single (singleton) form generator — Card-per-section with independent saves.
 * Top-level `section` fields each become a Card with their own useForm + useMutation.
 * Schemas without sections fall back to a single Card with all fields.
 */

import { isLayoutField } from '@core-engine/schema/constants.js'
import { flattenFields, hasFieldType } from '@core-engine/schema/field-helpers/index.js'
import type { GeneratorOptions, Schema, SchemaField } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { pluralize, singularize, toCamelCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { isAuthorshipFieldName } from '@next-adapter/generators/actions/action-helpers.js'
import { generateFieldJSX } from '@next-adapter/generators/form/field-jsx.js'
import {
  buildDefaultValueFromSyncFunctions,
  buildDefaultValues,
  buildUiImports,
  buildZodFields,
  collectDefaultValueFromSourceNames,
  collectRelationshipFields,
  collectTabIconNames,
  type FormGeneratorResult,
  renderTabsTrigger
} from '@next-adapter/generators/form/form-shared.js'

// ============================================================================
// Card group parsing
// ============================================================================

interface CardGroup {
  title: string
  description: string
  hint: string
  varPrefix: string
  componentName: string
  fields: SchemaField[]
  flatFields: SchemaField[]
}

/**
 * Parse schema fields into card groups.
 * - Top-level `section` fields each become a card.
 * - Fields without sections → one card (backward compat).
 */
function parseCardGroups(allFormFields: SchemaField[], schemaLabel: string): CardGroup[] {
  const sections = allFormFields.filter((f) => f.type === 'section' && f.fields)
  const nonSections = allFormFields.filter((f) => f.type !== 'section' && !isLayoutField(f.type))

  // If no sections, all fields go in one card
  if (sections.length === 0) {
    const title = nonSections[0]?.label || nonSections[0]?.name || schemaLabel
    return [
      {
        title,
        description: '',
        hint: '',
        varPrefix: toCamelCase(nonSections[0]?.name || 'form'),
        componentName: `${toPascalCase(nonSections[0]?.name || 'form')}Card`,
        fields: nonSections,
        flatFields: flattenFields(nonSections).filter((f) => !isLayoutField(f.type))
      }
    ]
  }

  // Each section becomes a card
  const groups: CardGroup[] = []
  for (const section of sections) {
    const innerFields = section.fields || []
    groups.push({
      title: section.label || section.name,
      description: section.description || '',
      hint: section.hint || '',
      varPrefix: toCamelCase(section.name),
      componentName: `${toPascalCase(section.name)}Card`,
      fields: innerFields,
      flatFields: flattenFields(innerFields).filter((f) => !isLayoutField(f.type))
    })
  }

  // If there are also loose fields outside sections, put them in a default card
  if (nonSections.length > 0) {
    const title = nonSections[0]?.label || nonSections[0]?.name || 'General'
    groups.unshift({
      title,
      description: '',
      hint: '',
      varPrefix: toCamelCase(nonSections[0]?.name || 'general'),
      componentName: `${toPascalCase(nonSections[0]?.name || 'General')}Card`,
      fields: nonSections,
      flatFields: flattenFields(nonSections).filter((f) => !isLayoutField(f.type))
    })
  }

  return groups
}

// ============================================================================
// Per-group analysis
// ============================================================================

interface GroupAnalysis {
  relFields: SchemaField[]
  multiSelectFields: SchemaField[]
  listFieldsWithNested: SchemaField[]
  hasNestedObjectList: boolean
  hasTabsField: boolean
  tabFieldNames: Set<string>
}

function hasNestedObjectListField(fields: SchemaField[], insideList = false): boolean {
  for (const field of fields) {
    if (field.hidden) continue
    if (field.type === 'list') {
      if (insideList && field.fields && field.fields.length > 0) return true
      if (field.fields && hasNestedObjectListField(field.fields, true)) return true
    }
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      if (hasNestedObjectListField(field.fields, insideList)) return true
    }
    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        if (hasNestedObjectListField(tab.fields ?? [], insideList)) return true
      }
    }
  }

  return false
}

function hasSimpleListField(fields: SchemaField[]): boolean {
  for (const field of fields) {
    if (field.hidden) continue
    if (field.type === 'list') {
      if (!field.fields || field.fields.length === 0) return true
      if (hasSimpleListField(field.fields)) return true
    }
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      if (hasSimpleListField(field.fields)) return true
    }
    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        if (hasSimpleListField(tab.fields ?? [])) return true
      }
    }
  }

  return false
}

function analyzeGroup(fields: SchemaField[]): GroupAnalysis {
  const tabFieldNames = new Set<string>()
  for (const f of fields) {
    if (f.type === 'tabs' && f.tabs) {
      for (const tab of f.tabs) {
        if (tab.fields) for (const tf of tab.fields) tabFieldNames.add(tf.name)
      }
    }
  }

  const relFields = collectRelationshipFields(fields, tabFieldNames)
  const multiSelectFields = flattenFields(fields).filter(
    (f) => f.type === 'select' && f.multiple === true
  )

  const listFieldsWithNested: SchemaField[] = []
  function collectLists(flds: SchemaField[]): void {
    for (const f of flds) {
      if (f.type === 'list' && f.fields && f.fields.length > 0 && !f.hidden) {
        listFieldsWithNested.push(f)
      }
      if (f.type === 'group' && f.fields) collectLists(f.fields)
      if (f.type === 'tabs' && f.tabs) {
        for (const tab of f.tabs) {
          if (tab.fields) collectLists(tab.fields)
        }
      }
    }
  }
  collectLists(fields)

  const hasTabsField = fields.some((f) => f.type === 'tabs')

  return {
    relFields,
    multiSelectFields,
    listFieldsWithNested,
    hasNestedObjectList: hasNestedObjectListField(fields),
    hasTabsField,
    tabFieldNames
  }
}

// ============================================================================
// Per-group JSX generation
// ============================================================================

function buildGroupFieldsJSX(
  group: CardGroup,
  analysis: GroupAnalysis,
  defaultValueFromSourceNames: Set<string>,
  creatableSelectFieldNames: Set<string>
): string {
  const indent = '            '
  return group.fields
    .map((f) => {
      if (f.type === 'tabs' && f.tabs) {
        const tabsList = f.tabs.map((t) => renderTabsTrigger(t, '              ')).join('\n')
        const tabsContent = f.tabs
          .map((t) => {
            const tabFields = (t.fields || [])
              .map((tf) =>
                generateFieldJSX(tf, '                ', {
                  creatableSelectFieldNames,
                  defaultValueFromSourceNames
                })
              )
              .join('\n')
            return `              <TabsContent value="${t.name}" className="space-y-6">
${tabFields}
              </TabsContent>`
          })
          .join('\n')
        return `${indent}<Tabs value={activeTab} onValueChange={(value) => void setActiveTab(value as ${toPascalCase(group.varPrefix)}TabValue)} className="w-full">
              <TabsList>
${tabsList}
              </TabsList>
${tabsContent}
${indent}</Tabs>`
      }
      if (analysis.tabFieldNames.has(f.name)) return ''
      return generateFieldJSX(f, indent, {
        creatableSelectFieldNames,
        defaultValueFromSourceNames
      })
    })
    .filter(Boolean)
    .join('\n')
}

function buildGroupRelState(analysis: GroupAnalysis): string {
  const relationshipOpenState = analysis.relFields
    .map((f) => {
      return `  const [${f.name}Open, set${toPascalCase(f.name)}Open] = React.useState(false)`
    })
    .join('\n')
  const relationshipDataState = Array.from(
    new Set(analysis.relFields.map((field) => field.relationship).filter(Boolean))
  )
    .map((relationship) => {
      const relPlural = toPascalCase(pluralize(relationship || ''))
      return `  const { data: ${relationship}Data } = use${relPlural}()`
    })
    .join('\n')

  return [relationshipOpenState, relationshipDataState].filter(Boolean).join('\n')
}

function buildGroupSelectState(fields: SchemaField[]): string {
  return fields
    .map(
      (field) =>
        `  const [${field.name}Open, set${toPascalCase(field.name)}Open] = React.useState(false)`
    )
    .join('\n')
}

function buildGroupCreatableSelectState(
  fields: SchemaField[],
  schema: Schema,
  Singular: string
): string {
  return fields
    .map((field) => {
      const fieldPascal = toPascalCase(field.name)
      const staticOptions = (field.options ?? [])
        .map(
          (option) =>
            `{ label: ${JSON.stringify(option.label)}, value: ${JSON.stringify(option.value)} }`
        )
        .join(', ')
      return `  const [${field.name}Open, set${fieldPascal}Open] = React.useState(false)
  const [${field.name}Search, set${fieldPascal}Search] = React.useState('')
  const [create${fieldPascal}OptionPending, startCreate${fieldPascal}OptionTransition] = React.useTransition()
  const { data: ${field.name}CreatedOptions = [] as ${Singular}SelectOption[] } = useQuery<${Singular}SelectOption[]>({
    queryKey: ['${schema.name}', 'select-options', '${field.name}'],
    queryFn: () => get${Singular}SelectOptions('${field.name}')
  })
  const ${field.name}StaticOptions: Array<{ label: string; value: string }> = [${staticOptions}]
  const ${field.name}Options = [
    ...${field.name}StaticOptions,
    ...${field.name}CreatedOptions.filter(
      (createdOption: ${Singular}SelectOption) =>
        !${field.name}StaticOptions.some((option) => option.value === createdOption.value)
    )
  ]`
    })
    .join('\n')
}

function buildGroupFieldArrayHooks(analysis: GroupAnalysis): string {
  return analysis.listFieldsWithNested
    .map((field) => {
      const pascalFieldName = toPascalCase(field.name)
      return `  const [${field.name}Expanded, set${pascalFieldName}Expanded] = React.useState('')
  const ${field.name}FieldArray = useFieldArray({
    control: form.control,
    name: '${field.name}'
  })`
    })
    .join('\n')
}

function buildGroupCreatableSelectHandlers(
  fields: SchemaField[],
  schema: Schema,
  Singular: string,
  defaultValueFromSourceNames: Set<string>
): string {
  return fields
    .map((field) => {
      const fieldPascal = toPascalCase(field.name)
      const defaultValueFromHandler = defaultValueFromSourceNames.has(field.name)
        ? `applyDefaultValueFrom${fieldPascal}`
        : null
      const setCreatedOptionValue = field.multiple
        ? `const currentValue = form.getValues('${field.name}')
      const selectedValues = Array.isArray(currentValue) ? currentValue : []
      const nextValues = [...new Set([...selectedValues, result.option.value])]
      ${defaultValueFromHandler ? `${defaultValueFromHandler}(nextValues)\n      ` : ''}form.setValue('${field.name}', nextValues, {
        shouldDirty: true,
        shouldValidate: true
      })`
        : `${defaultValueFromHandler ? `${defaultValueFromHandler}(result.option.value)\n      ` : ''}form.setValue('${field.name}', result.option.value, {
        shouldDirty: true,
        shouldValidate: true
      })`
      return `  function handleCreate${fieldPascal}Option(label: string) {
    const trimmedLabel = label.trim()
    if (!trimmedLabel) return

    startCreate${fieldPascal}OptionTransition(async () => {
      const result = await create${Singular}SelectOption({
        fieldName: '${field.name}',
        label: trimmedLabel
      })

      if (!result.success || !result.option) {
        toast.error(result.error || 'Failed to create option')
        return
      }

      await queryClient.invalidateQueries({
        queryKey: ['${schema.name}', 'select-options', '${field.name}']
      })
      ${setCreatedOptionValue}
      set${fieldPascal}Search('')
      set${fieldPascal}Open(false)
      toast.success('Option created')
    })
  }`
    })
    .join('\n\n')
}

// ============================================================================
// Card component generation
// ============================================================================

function generateCardComponent(
  group: CardGroup,
  schema: Schema,
  analysis: GroupAnalysis,
  allFlatFields: SchemaField[]
): string {
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)

  const zodFields = buildZodFields(group.flatFields)
  const defaultValues = buildDefaultValues(group.flatFields, 'initialData', allFlatFields)
  const defaultValueFromSourceNames = collectDefaultValueFromSourceNames(group.flatFields)
  const defaultValueFromSyncFunctions = buildDefaultValueFromSyncFunctions(group.flatFields)
  const creatableSelectFields = group.flatFields.filter(
    (field) => field.type === 'select' && field.creatable === true
  )
  const creatableSelectFieldNames = new Set(creatableSelectFields.map((field) => field.name))
  const fieldsJSX = buildGroupFieldsJSX(
    group,
    analysis,
    defaultValueFromSourceNames,
    creatableSelectFieldNames
  )
  const relState = buildGroupRelState(analysis)
  const selectState = buildGroupSelectState(
    analysis.multiSelectFields.filter((field) => field.creatable !== true)
  )
  const creatableSelectState = buildGroupCreatableSelectState(
    creatableSelectFields,
    schema,
    Singular
  )
  const creatableSelectHandlers = buildGroupCreatableSelectHandlers(
    creatableSelectFields,
    schema,
    Singular,
    defaultValueFromSourceNames
  )
  const fieldArrayHooks = buildGroupFieldArrayHooks(analysis)
  const savedValueName = `saved${Singular}`
  const resetValues = buildDefaultValues(group.flatFields, savedValueName, allFlatFields).replace(
    /^ {6}/gm,
    '        '
  )

  const descriptionLine = group.description
    ? `\n            <CardDescription>${group.description}</CardDescription>`
    : ''
  const tabField = group.fields.find((field) => field.type === 'tabs')
  const tabNames = tabField?.tabs?.map((tab) => tab.name) ?? []
  const tabParserConst = analysis.hasTabsField
    ? `
const ${group.varPrefix}TabValues = [${tabNames.map((name) => JSON.stringify(name)).join(', ')}] as const
const ${group.varPrefix}TabParser = parseAsStringLiteral(${group.varPrefix}TabValues).withDefault(${group.varPrefix}TabValues[0])

type ${toPascalCase(group.varPrefix)}TabValue = (typeof ${group.varPrefix}TabValues)[number]
`
    : ''

  return `const ${group.varPrefix}Schema = z.object({
${zodFields}
})
${tabParserConst}

function ${group.componentName}({ initialData }: { initialData?: ${Singular} | null }) {
  const queryClient = useQueryClient()${analysis.hasTabsField ? `\n  const [activeTab, setActiveTab] = useQueryState('tab', ${group.varPrefix}TabParser)` : ''}${relState ? `\n${relState}` : ''}${creatableSelectState ? `\n${creatableSelectState}` : ''}${selectState ? `\n${selectState}` : ''}

  const form = useForm<z.infer<typeof ${group.varPrefix}Schema>>({
    resolver: standardSchemaResolver(${group.varPrefix}Schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
${defaultValues}
    }
  })

  const mutation = useMutation({
    mutationFn: (data: ${Singular}UpsertInput) => upsert${Singular}(data),
    onSuccess: async (result) => {
      const ${savedValueName} = result.${toCamelCase(singular)}
      if (${savedValueName}) {
        form.reset({
${resetValues}
        })
      }
      toast.success('${group.title} saved')
      void queryClient.invalidateQueries({ queryKey: ['${schema.name}'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save')
    }
  })

  const isPending = mutation.isPending
${defaultValueFromSyncFunctions ? `\n${defaultValueFromSyncFunctions}\n` : ''}
${fieldArrayHooks ? `\n${fieldArrayHooks}\n` : ''}
${creatableSelectHandlers ? `${creatableSelectHandlers}\n\n` : ''}
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => {
        mutation.mutate(values as ${Singular}UpsertInput)
      })}>
        <Card >
          <CardHeader>
            <CardTitle>${group.title}</CardTitle>${descriptionLine}
          </CardHeader>
          <CardContent className="space-y-5">
${fieldsJSX}
          </CardContent>
          <CardFooter>${group.hint ? `\n            <p className="text-sm text-muted-foreground">${group.hint}</p>` : ''}
            <Button type="submit" disabled={isPending || !form.formState.isDirty} className="ml-auto min-w-25">
              {isPending && <LoaderCircle className="animate-spin" />}
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  )
}`
}

function fieldRendersMainJSXForImports(field: SchemaField): boolean {
  if (field.hidden) return false
  if ((field.type === 'group' || field.type === 'section') && field.fields) {
    return field.fields.some(fieldRendersMainJSXForImports)
  }
  if (field.type === 'tabs') return Boolean(field.tabs?.length)
  return true
}

function fieldSupportsIconPostfix(field: SchemaField): boolean {
  return !['group', 'section', 'tabs', 'separator'].includes(field.type)
}

function hasRenderedIconPostfix(fields: SchemaField[]): boolean {
  for (const field of fields) {
    if (field.hidden) continue
    if (field.hasIcon && fieldSupportsIconPostfix(field) && fieldRendersMainJSXForImports(field)) {
      return true
    }
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      if (hasRenderedIconPostfix(field.fields)) return true
    }
    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        if (hasRenderedIconPostfix(tab.fields ?? [])) return true
      }
    }
  }
  return false
}

// ============================================================================
// Main generator
// ============================================================================

export function generateSingleForm(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): FormGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)

  // Filter out auto-generated fields
  const allFormFields = schema.fields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      !isAuthorshipFieldName(f.name)
  )

  // Parse into card groups
  const cardGroups = parseCardGroups(allFormFields, schema.label)

  // --- Global field analysis for imports ---
  const hasBoolean = hasFieldType(schema.fields, 'boolean')
  const hasImage = hasFieldType(schema.fields, 'image')
  const hasVideo = hasFieldType(schema.fields, 'video')
  const hasMedia = hasFieldType(schema.fields, 'media')
  const hasGallery = hasFieldType(schema.fields, 'gallery')
  const hasIcon = hasFieldType(schema.fields, 'icon')
  const hasIconPostfix = hasRenderedIconPostfix(allFormFields)
  const hasDate = hasFieldType(schema.fields, 'date')
  const flatFields = flattenFields(allFormFields)
  const hasMultiSelect = flatFields.some((f) => f.type === 'select' && f.multiple === true)
  const hasCreatableSelect = flatFields.some((f) => f.type === 'select' && f.creatable === true)
  const hasSelectCombobox = hasMultiSelect || hasCreatableSelect
  const hasSelect = flatFields.some(
    (f) => f.type === 'select' && f.multiple !== true && f.creatable !== true
  )
  const hasMarkdown = hasFieldType(schema.fields, 'markdown')
  const hasRichtext = hasFieldType(schema.fields, 'richtext')
  const hasTextarea = hasFieldType(schema.fields, 'text')
  const hasSeparator = hasFieldType(schema.fields, 'separator')
  const hasRelationship = hasFieldType(schema.fields, 'relationship')
  const hasTabsField = cardGroups.some((group) => analyzeGroup(group.fields).hasTabsField)
  const hasList = hasFieldType(schema.fields, 'list')
  const hasNestedList = cardGroups.some(
    (g) => analyzeGroup(g.fields).listFieldsWithNested.length > 0
  )
  const hasNestedObjectList = cardGroups.some(
    (group) => analyzeGroup(group.fields).hasNestedObjectList
  )
  const hasSimpleList = hasList && hasSimpleListField(allFormFields)
  const tabIconNames = collectTabIconNames(allFormFields)

  // Global relationship hooks for imports
  const allRelFields: SchemaField[] = []
  for (const g of cardGroups) {
    const a = analyzeGroup(g.fields)
    for (const rf of a.relFields) {
      if (!allRelFields.some((e) => e.relationship === rf.relationship)) {
        allRelFields.push(rf)
      }
    }
  }

  const needsReact = allRelFields.length > 0 || hasNestedList || hasSelectCombobox

  // --- Build imports ---
  const uiImports = buildUiImports({
    hasBoolean,
    hasTextarea,
    hasImage,
    hasVideo,
    hasMedia,
    hasGallery,
    hasIcon,
    hasIconPostfix,
    hasDate,
    hasMarkdown,
    hasRichtext,
    hasSeparator,
    hasSelect,
    hasSelectCombobox,
    hasCreatableSelect,
    hasMultiSelect,
    hasTabsField,
    hasRelationship,
    hasSimpleList,
    hasNestedList,
    hasNestedObjectList
  })

  // Add Card imports
  const cardImportIndex = uiImports.indexOf("import { Card } from '@admin/components/ui/card'")
  if (cardImportIndex !== -1) {
    uiImports.splice(cardImportIndex, 1)
  }
  uiImports.push(`import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@admin/components/ui/card'`)

  // Lucide icons
  const lucideIcons: string[] = []
  const addLucideIcons = (...icons: string[]) => {
    for (const icon of icons) {
      if (!lucideIcons.includes(icon)) lucideIcons.push(icon)
    }
  }
  addLucideIcons('LoaderCircle', ...tabIconNames)
  if (hasRelationship || hasSelectCombobox) addLucideIcons('Check', 'ChevronsUpDown')
  if (hasCreatableSelect) addLucideIcons('Plus')
  if (hasNestedList) {
    addLucideIcons('Plus', 'X')
  }

  // Relationship hook imports
  const relHookImports = allRelFields
    .map((f) => {
      const relPlural = toPascalCase(pluralize(f.relationship || ''))
      return `import { use${relPlural} } from '@admin/hooks/use-${f.relationship}'`
    })
    .filter((v, i, a) => a.indexOf(v) === i)
    .join('\n')

  // --- Generate per-group card components ---
  const cardComponents = cardGroups.map((group) => {
    const analysis = analyzeGroup(group.fields)
    return generateCardComponent(group, schema, analysis, flatFields)
  })

  // --- Generate parent component ---
  const cardRenders = cardGroups
    .map((g) => `        <${g.componentName} initialData={initialData} />`)
    .join('\n')

  // --- Assemble file ---
  const content = `'use client'
${needsReact ? "\nimport * as React from 'react'" : ''}
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { useMutation, ${hasCreatableSelect ? 'useQuery, ' : ''}useQueryClient } from '@tanstack/react-query'${lucideIcons.length > 0 ? `\nimport { ${lucideIcons.join(', ')} } from 'lucide-react'` : ''}
import {${hasNestedList ? ' useFieldArray,' : ''} useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v3'${hasTabsField ? "\nimport { parseAsStringLiteral, useQueryState } from 'nuqs'" : ''}${hasRelationship || hasSelectCombobox ? "\nimport { cn } from '@admin/utils/shared/cn'" : ''}
${relHookImports ? `${relHookImports}\n` : ''}${uiImports.join('\n')}
import type {
  ${Singular},
  ${hasCreatableSelect ? `${Singular}SelectOption,\n  ` : ''}${Singular}UpsertInput
} from '@admin/actions/${actionImportPath}'
import { ${hasCreatableSelect ? `create${Singular}SelectOption, get${Singular}SelectOptions, ` : ''}upsert${Singular} } from '@admin/actions/${actionImportPath}'

${cardComponents.join('\n\n')}

interface ${Singular}FormProps {
  initialData?: ${Singular} | null
}

export function ${Singular}Form({ initialData }: ${Singular}FormProps) {
  return (
    <div className="space-y-6">
${cardRenders}
    </div>
  )
}
`

  return {
    files: [createGeneratedFile(`${pagesDir}/${schema.name}/${schema.name}-form.tsx`, content)]
  }
}
